
import { calculateVariability } from './helpers';
import type { ExpenseOptimization } from './types';

/**
 * AI-powered expense optimization
 */
export function aiExpenseOptimization(
  historicalExpenses: any[],
  assumptions: any
): ExpenseOptimization {
  const categories = ['operating', 'administrative', 'marketing', 'research'];
  const savingOpportunities: Array<{ category: string; potential: number; confidence: number }> = [];
  const recommendations: string[] = [];
  
  // Analyze expense patterns and identify optimization opportunities
  categories.forEach(category => {
    const categoryExpenses = historicalExpenses.filter(exp => 
      exp.category?.toLowerCase().includes(category)
    );
    
    if (categoryExpenses.length > 0) {
      const avgExpense = categoryExpenses.reduce((sum, exp) => sum + exp.amount, 0) / categoryExpenses.length;
      const variability = calculateVariability(categoryExpenses.map(exp => exp.amount));
      
      // High variability suggests optimization potential
      if (variability > 0.2) {
        const potentialSaving = avgExpense * 0.15; // Assume 15% optimization potential
        savingOpportunities.push({
          category,
          potential: potentialSaving,
          confidence: Math.min(0.8, 1 - variability)
        });
        
        recommendations.push(`Consider optimizing ${category} expenses - high variability detected`);
      }
    }
  });
  
  // Generate optimized expense projections
  const baseGrowthRate = assumptions.profitLoss?.operatingExpenseGrowth || 5;
  const optimizationFactor = 0.95; // 5% optimization target
  
  const optimizedExpenses = Array.from({ length: 12 }, (_, i) => {
    const baseExpense = (historicalExpenses[historicalExpenses.length - 1]?.amount || 10000);
    const growthAdjustment = Math.pow(1 + baseGrowthRate / 100, i + 1);
    return baseExpense * growthAdjustment * optimizationFactor;
  });
  
  return {
    optimizedExpenses,
    savingOpportunities,
    recommendations
  };
}
